import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Play, Pause, Headphones, Download, Smartphone, Infinity } from 'lucide-react';
import { Button } from './ui/Button';
import { motion } from 'framer-motion';
const COVER_IMAGE = 'https://cdn.magicpatterns.com/uploads/4K3DGajw8RBxZt9YF86Wxj/ChatGPT_Image_Feb_6,_2026,_05_01_10_PM.png';
const AUDIO_SRC = 'https://cdn.shopify.com/s/files/1/0586/4069/5378/files/The_Taproot_Protocol_-_Tiaser01.wav?v=1770409209';
const BAR_COUNT = 64;
const BAR_GAP = 2;
export function HeroSection() {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<MediaElementAudioSourceNode | null>(null);
  const animFrameRef = useRef<number>(0);
  const dataArrayRef = useRef<Uint8Array | null>(null);
  // Initialize Web Audio API on first play
  const initAudioContext = useCallback(() => {
    if (audioContextRef.current || !audioRef.current) return;
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.75;
    const source = ctx.createMediaElementSource(audioRef.current);
    source.connect(analyser);
    analyser.connect(ctx.destination);
    audioContextRef.current = ctx;
    analyserRef.current = analyser;
    sourceRef.current = source;
    dataArrayRef.current = new Uint8Array(analyser.frequencyBinCount);
  }, []);
  // Draw waveform visualization
  const drawVisualization = useCallback(() => {
    const canvas = canvasRef.current;
    const analyser = analyserRef.current;
    const dataArray = dataArrayRef.current;
    if (!canvas || !analyser || !dataArray) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const draw = () => {
      animFrameRef.current = requestAnimationFrame(draw);
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      if (canvas.width !== rect.width * dpr || canvas.height !== rect.height * dpr) {
        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;
        ctx.scale(dpr, dpr);
      }
      const width = rect.width;
      const height = rect.height;
      ctx.clearRect(0, 0, width, height);
      analyser.getByteFrequencyData(dataArray);
      const barWidth = (width - (BAR_COUNT - 1) * BAR_GAP) / BAR_COUNT;
      const progressPct = duration > 0 ? currentTime / duration : 0;
      for (let i = 0; i < BAR_COUNT; i++) {
        // Map bar index to frequency data (use lower half for more musical response)
        const dataIndex = Math.floor(i / BAR_COUNT * (dataArray.length * 0.7));
        const value = dataArray[dataIndex] / 255;
        // Minimum bar height for idle state
        const minHeight = 3;
        const maxBarHeight = height * 0.9;
        const barHeight = Math.max(minHeight, value * maxBarHeight);
        const x = i * (barWidth + BAR_GAP);
        const y = (height - barHeight) / 2;
        const barPct = i / BAR_COUNT;
        // Gold color with varying opacity based on played progress
        if (barPct <= progressPct) {
          // Played portion — bright gold
          ctx.fillStyle = `rgba(212, 168, 83, ${0.7 + value * 0.3})`;
        } else {
          // Unplayed portion — dim gold
          ctx.fillStyle = `rgba(212, 168, 83, ${0.15 + value * 0.15})`;
        }
        // Rounded bars
        const radius = Math.min(barWidth / 2, 2);
        ctx.beginPath();
        ctx.moveTo(x + radius, y);
        ctx.lineTo(x + barWidth - radius, y);
        ctx.quadraticCurveTo(x + barWidth, y, x + barWidth, y + radius);
        ctx.lineTo(x + barWidth, y + barHeight - radius);
        ctx.quadraticCurveTo(x + barWidth, y + barHeight, x + barWidth - radius, y + barHeight);
        ctx.lineTo(x + radius, y + barHeight);
        ctx.quadraticCurveTo(x, y + barHeight, x, y + barHeight - radius);
        ctx.lineTo(x, y + radius);
        ctx.quadraticCurveTo(x, y, x + radius, y);
        ctx.fill();
      }
    };
    draw();
  }, [currentTime, duration]);
  // Draw idle/static bars when not playing
  const drawIdleBars = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);
    const width = rect.width;
    const height = rect.height;
    const barWidth = (width - (BAR_COUNT - 1) * BAR_GAP) / BAR_COUNT;
    const progressPct = duration > 0 ? currentTime / duration : 0;
    ctx.clearRect(0, 0, width, height);
    for (let i = 0; i < BAR_COUNT; i++) {
      // Generate a pseudo-random but consistent height for each bar (looks like a waveform shape)
      const seed = Math.sin(i * 0.5 + 1.5) * 0.5 + 0.5;
      const barHeight = Math.max(3, seed * height * 0.5 + 4);
      const x = i * (barWidth + BAR_GAP);
      const y = (height - barHeight) / 2;
      const barPct = i / BAR_COUNT;
      if (barPct <= progressPct) {
        ctx.fillStyle = 'rgba(212, 168, 83, 0.7)';
      } else {
        ctx.fillStyle = 'rgba(212, 168, 83, 0.2)';
      }
      const radius = Math.min(barWidth / 2, 2);
      ctx.beginPath();
      ctx.moveTo(x + radius, y);
      ctx.lineTo(x + barWidth - radius, y);
      ctx.quadraticCurveTo(x + barWidth, y, x + barWidth, y + radius);
      ctx.lineTo(x + barWidth, y + barHeight - radius);
      ctx.quadraticCurveTo(x + barWidth, y + barHeight, x + barWidth - radius, y + barHeight);
      ctx.lineTo(x + radius, y + barHeight);
      ctx.quadraticCurveTo(x, y + barHeight, x, y + barHeight - radius);
      ctx.lineTo(x, y + radius);
      ctx.quadraticCurveTo(x, y, x + radius, y);
      ctx.fill();
    }
  }, [currentTime, duration]);
  // Start/stop visualization loop
  useEffect(() => {
    if (isPlaying && analyserRef.current) {
      drawVisualization();
    } else {
      cancelAnimationFrame(animFrameRef.current);
      drawIdleBars();
    }
    return () => cancelAnimationFrame(animFrameRef.current);
  }, [isPlaying, drawVisualization, drawIdleBars]);
  // Draw idle bars on mount and when progress changes while paused
  useEffect(() => {
    if (!isPlaying) {
      drawIdleBars();
    }
  }, [currentTime, duration, isPlaying, drawIdleBars]);
  const scrollToWhySeven = () => {
    document.getElementById('why-seven')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const togglePlay = () => {
    if (!audioRef.current) return;
    initAudioContext();
    if (audioContextRef.current?.state === 'suspended') {
      audioContextRef.current.resume();
    }
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };
  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };
  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };
  const handleSeek = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!audioRef.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const pct = x / rect.width;
    audioRef.current.currentTime = pct * duration;
    setCurrentTime(pct * duration);
  };
  const handleEnded = () => {
    setIsPlaying(false);
    cancelAnimationFrame(animFrameRef.current);
  };
  const formatTime = (t: number) => {
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };
  return <div className="w-full bg-night-950 pt-10 pb-12 md:pt-16 md:pb-16 px-6 relative overflow-hidden" data-id="element-17">
      <audio ref={audioRef} src={AUDIO_SRC} preload="metadata" crossOrigin="anonymous" onTimeUpdate={handleTimeUpdate} onLoadedMetadata={handleLoadedMetadata} onEnded={handleEnded} data-id="element-18" />

      {/* Background ambient glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-4xl h-[500px] bg-gold-500/5 blur-[120px] rounded-full pointer-events-none" data-id="element-19" />

      <div className="max-w-3xl mx-auto text-center relative z-10" data-id="element-20">
        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} className="text-gold-500 font-medium tracking-widest text-sm uppercase mb-6" data-id="element-21">
          The Taproot Protocol
        </motion.p>

        <motion.h1 initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4
      }} className="font-serif text-4xl md:text-6xl lg:text-7xl text-white font-medium leading-tight mb-6" data-id="element-22">
          You've Tried Everything for Your Insomnia.{' '}
          <span className="text-slate-400" data-id="element-23">Nothing Worked.</span>
        </motion.h1>

        <motion.h2 initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.6
      }} className="font-serif text-2xl md:text-3xl text-gold-500 italic mb-8" data-id="element-24">
          Here's Why.
        </motion.h2>

        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.8
      }} className="text-slate-400 text-lg md:text-xl leading-relaxed max-w-2xl mx-auto mb-10" data-id="element-25">
          The real cause of chronic insomnia isn't a sleep problem at all. It's
          buried three layers deeper than any sleep expert has asked you to
          look.
        </motion.p>

        {/* Cover Image */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.9
      }} className="mb-10" data-id="element-26">
          <img src={COVER_IMAGE} alt="The Sleep Fix — The Taproot Protocol" className="w-60 h-60 md:w-72 md:h-72 rounded-xl shadow-2xl mx-auto border border-gold-500/20 object-cover" data-id="element-27" />
          <p className="text-slate-500 text-xs mt-3 italic" data-id="element-28">
            Press play to hear the revelation that changes everything.
          </p>
        </motion.div>

        {/* Audio Player Card */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 1.0
      }} className="bg-night-800 border border-gold-500/30 rounded-lg p-5 md:p-6 mb-10 shadow-2xl relative overflow-hidden" data-id="element-29">
          <div className="absolute top-0 left-0 w-1 h-full bg-gold-500" data-id="element-30" />

          <div className="flex items-center gap-4 mb-2" data-id="element-31">
            <button onClick={togglePlay} className="w-14 h-14 rounded-full bg-gold-500 flex items-center justify-center flex-shrink-0 shadow-[0_0_15px_rgba(212,168,83,0.4)] hover:scale-105 transition-transform focus:outline-none" data-id="element-32">
              {isPlaying ? <Pause className="w-6 h-6 text-night-950" fill="currentColor" data-id="element-33" /> : <Play className="w-6 h-6 text-night-950 ml-0.5" fill="currentColor" data-id="element-34" />}
            </button>

            <div className="flex-1" data-id="element-35">
              <div className="flex justify-between items-center mb-2" data-id="element-36">
                <span className="text-xs text-gold-500 font-bold tracking-wider uppercase" data-id="element-37">
                  Audio Preview
                </span>
                <span className="text-xs text-slate-400 font-mono" data-id="element-38">
                  {formatTime(currentTime)} /{' '}
                  {duration > 0 ? formatTime(duration) : '—:——'}
                </span>
              </div>

              {/* Waveform Visualizer Canvas */}
              <canvas ref={canvasRef} className="w-full h-12 cursor-pointer rounded" onClick={handleSeek} data-id="element-39" />
            </div>
          </div>

          <div className="text-left border-t border-white/10 pt-4 mt-2" data-id="element-40">
            <p className="font-serif italic text-lg text-white mb-3" data-id="element-41">
              "Why Every Sleep Solution Stops Three Layers Too Shallow"
            </p>
            <p className="text-slate-400 text-sm leading-relaxed mb-5" data-id="element-42">
              In the first moments of this audio, you'll discover why melatonin
              worked for three nights then stopped, why CBT-I helped for six
              weeks before everything returned, and why every solution you've
              tried has been treating symptoms as if they were roots.
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-id="element-43">
              <div className="flex flex-col items-center text-center gap-1.5" data-id="element-44">
                <Headphones className="w-4 h-4 text-gold-500" data-id="element-45" />
                <span className="text-xs text-slate-300" data-id="element-46">40 min Audio</span>
              </div>
              <div className="flex flex-col items-center text-center gap-1.5" data-id="element-47">
                <Download className="w-4 h-4 text-gold-500" data-id="element-48" />
                <span className="text-xs text-slate-300" data-id="element-49">Instant MP3</span>
              </div>
              <div className="flex flex-col items-center text-center gap-1.5" data-id="element-50">
                <Smartphone className="w-4 h-4 text-gold-500" data-id="element-51" />
                <span className="text-xs text-slate-300" data-id="element-52">Any Device</span>
              </div>
              <div className="flex flex-col items-center text-center gap-1.5" data-id="element-53">
                <Infinity className="w-4 h-4 text-gold-500" data-id="element-54" />
                <span className="text-xs text-slate-300" data-id="element-55">Lifetime Access</span>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 1.2
      }} data-id="element-56">
          <Button onClick={scrollToWhySeven} size="lg" glow className="w-full md:w-auto min-w-[250px]" data-id="element-57">
            SEE WHY IT'S JUST $7 ↓
          </Button>
        </motion.div>
      </div>
    </div>;
}